import React, { useCallback, useState } from 'react';
import FileError from './FileError';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  faTrash,
  faExclamation,
  faFile,
  faPlus,
  faCircleCheck,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { mergeClassNames } from '../../utils/mergeClassNames';

export interface TrueImpactFile {
  fileName?: string; // for backend
  file?: File;
  error?: string;
  description?: string;
  id?: number;
  url?: string;
  isUploading?: boolean;
}

const StyledDiv = styled.div<{
  isError: boolean;
  isFile: boolean;
  isDragging: boolean;
  width: string;
  height: string;
  padding: string;
}>`
  cursor: ${({ isFile }) => (isFile ? 'inherit' : 'pointer')};
  border-radius: 4px;
  margin-bottom: 14px;
  transition: background 0.5s;
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  padding: ${({ padding }) => padding};
  background-color: ${({ isDragging }) =>
    isDragging ? COLORS.ctaPrimary : 'inherit'};
  border: 1px ${({ isFile }) => (isFile ? 'solid ' : 'dashed ')}
    ${({ isError }) => (isError ? COLORS.critical : COLORS.ctaPrimary)};
  * {
    color: ${({ isDragging }) => (isDragging ? COLORS.whiteBase : 'inherit')};
  }
  &:hover {
    background-color: ${({ isError }) =>
      isError ? COLORS.grayLightest : COLORS.grayLightest};
  }
  input[type='file'] {
    display: none;
  }
  .add-file-icon {
    margin-bottom: 12px;
  }
  .drag-text {
    max-width: 168px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.small};
    .file-name {
      display: flex;
      align-items: center;
      svg {
        border-radius: 100%;
        margin-left: ${SPACERS.extraTiny};
        path {
          fill: ${COLORS.success};
        }
      }
    }
  }
  .icon-container {
    display: flex;
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    align-items: center;
    border-radius: 100%;
    justify-content: center;
    background-color: ${({ isError, isFile }) =>
      isError
        ? COLORS.criticalBackground
        : isFile
        ? COLORS.ctaPrimary
        : COLORS.ctaPrimaryBackground};
    svg path {
      fill: ${({ isError, isFile }) =>
        isError
          ? COLORS.critical
          : isFile
          ? COLORS.whiteBase
          : COLORS.ctaPrimary};
    }
  }
  .delete-button {
    display: flex;
    align-items: center;
    cursor: pointer;
    .icon {
      margin-right: ${SPACERS.extraTiny};
    }
  }
`;

const defaultText = 'Drag your file here or click to upload';

interface TrueImpactFileUploadBoxProps {
  width?: string;
  height?: string;
  padding?: string;
  className?: string;
  defaultFileText?: string;
  file?: TrueImpactFile;
  acceptedFileString: string;
  CustomSelectedComponent?: React.FC<
    TrueImpactFile & {
      handleFileDelete: () => unknown;
      handleFileUploadClick: () => unknown;
    }
  >;
  handleFileUpload: (file: File | undefined) => unknown;
  isUploading?: boolean;
}

const TrueImpactFileUploadBox: React.FC<TrueImpactFileUploadBoxProps> = ({
  file,
  width = '278px',
  height = '215px',
  defaultFileText,
  className,
  padding = SPACERS.medium,
  CustomSelectedComponent,
  acceptedFileString,
  handleFileUpload,
  isUploading,
}) => {
  const isError = Boolean(file?.error);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileUploadClick = useCallback(() => {
    inputRef.current?.click();
  }, [inputRef]);

  const onFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      handleFileUpload(file);
    },
    [handleFileUpload],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
    },
    [setIsDragging],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
    },
    [setIsDragging],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      handleFileUpload(file);
    },
    [setIsDragging, handleFileUpload],
  );

  const handleFileDelete = () => {
    if (inputRef.current) {
      inputRef.current.files = null;
      inputRef.current.value = '';
    }
    handleFileUpload(undefined);
  };

  const isFileSelected = Boolean(file?.fileName);
  const showCustomRender =
    CustomSelectedComponent && file && (file.isUploading || isFileSelected);

  return (
    <StyledDiv
      isError={isError}
      isFile={Boolean(isFileSelected)}
      isDragging={isDragging}
      width={width}
      height={height}
      padding={showCustomRender ? '0px' : padding}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={isFileSelected ? undefined : handleFileUploadClick}
      className={mergeClassNames(className, 'file-upload-area')}
    >
      <input ref={inputRef} type="file" onChange={onFileUpload} />
      {showCustomRender ? (
        file?.isUploading || isUploading ? (
          <TrueImpactText className="drag-text">
            <TrueImpactLoading />
            Uploading...
          </TrueImpactText>
        ) : (
          <CustomSelectedComponent
            {...file}
            handleFileDelete={handleFileDelete}
            handleFileUploadClick={handleFileUploadClick}
          />
        )
      ) : file?.isUploading || isUploading ? (
        <TrueImpactText className="drag-text">
          {' '}
          <TrueImpactLoading />
          Uploading...
        </TrueImpactText>
      ) : (
        <>
          <div className="icon-container">
            <FontAwesomeIcon
              size="2x"
              icon={isError ? faExclamation : isFileSelected ? faFile : faPlus}
              className="icon"
            />
          </div>
          {isError ? (
            <FileError file={file} />
          ) : (
            <TrueImpactText className="drag-text">
              {isFileSelected && file ? (
                <div className="file-name">
                  {file.fileName}
                  <FontAwesomeIcon icon={faCircleCheck} className="icon" />
                </div>
              ) : (
                defaultFileText || defaultText
              )}
            </TrueImpactText>
          )}
          {!isFileSelected ? (
            <TrueImpactText variant="body-small">
              Accepted file formats:
              <br />
              {acceptedFileString}
            </TrueImpactText>
          ) : (
            <TrueImpactText
              variant="body-small"
              className="delete-button"
              onClick={handleFileDelete}
            >
              <FontAwesomeIcon icon={faTrash} className="icon" />
              Delete
            </TrueImpactText>
          )}
        </>
      )}
    </StyledDiv>
  );
};

export default TrueImpactFileUploadBox;
